/**
 * Shared loader for the "current understanding" progress model — the twelve
 * topic groups, the weighted overall percent, and each topic's teaser blurb.
 *
 * Two surfaces render that model and must not drift: the `/discovery` Progress
 * tab (`DiscoverySection.loadProgressData`) and the understanding-map right
 * panel opened from the in-chat memory-update card
 * (`components/progressPanelDrillHelper.js`). Both fetch the same pair of
 * endpoints and apply the same blurb-fallback rule, so both live here once.
 *
 * Surface-specific fetches stay with their surface: the Progress tab's
 * read-only guided-recommendation peek is not part of this module (the panel
 * has no recommended tile, and the peek must never be issued speculatively).
 *
 * @module core/progressData
 */

import { API } from './api.js';
import { DiscoveryAPI } from './discoveryApi.js';

/**
 * A topic's teaser line: the topic-summary `cardBlurb` whenever it is
 * non-empty — STALE included, since a slightly stale portrait line reads
 * better than the generic registry description and the bulk endpoint enqueues
 * a background regen for us — otherwise the registry description.
 *
 * @param {{id: string, description?: string}} topic - Topic as `loadTopicProgress` shapes it
 * @param {Map<string, {cardBlurb?: string}>} [blurbs] - Topic-summary map keyed by topic id
 * @returns {string} Display blurb (unescaped); '' when neither source has text
 */
export function topicBlurbText(topic, blurbs) {
  return (blurbs?.get(topic.id)?.cardBlurb || '').trim()
    || topic.description
    || '';
}

/**
 * Fetch core progress + the bulk topic-summary teasers in one parallel round
 * and shape them into the topic model both surfaces render.
 *
 * `allSettled`, never `Promise.all`: the two have different failure
 * consequences. Core progress is the only fetch whose failure matters — it
 * rejects out of here so each caller can own its own error presentation. The
 * summaries endpoint is `requireAuth` (guests 401 there while core progress
 * succeeds), so its failure degrades silently to registry descriptions.
 *
 * @param {Object} [opts]
 * @param {DiscoveryAPI} [opts.discoveryAPI] - Inject an existing instance;
 *   defaults to a fresh one.
 * @returns {Promise<{topics: Array<Object>, overall: number, betaPathGate: boolean, blurbs: Map<string, Object>}>}
 * @throws {Error} Whatever `getCoreProgress` rejected with
 */
export async function loadTopicProgress({ discoveryAPI } = {}) {
  const api = discoveryAPI || new DiscoveryAPI();

  const [coreResult, summariesResult] = await Promise.allSettled([
    api.getCoreProgress(),
    API.getAllTopicSummaries()
  ]);

  // Teaser blurbs. `getAllTopicSummaries` resolves with `null` on a network
  // error, so the null-safe coercion is load-bearing even on the fulfilled
  // branch.
  const summaries = summariesResult.status === 'fulfilled' ? summariesResult.value : null;
  const blurbs = new Map(((summaries) || []).map(s => [s.topicId, s]));

  if (coreResult.status === 'rejected') throw coreResult.reason;
  const data = coreResult.value?.data;

  // BETA PATH GATE — remove after beta; see backend/src/config/betaPathGate.js
  // Read the master gate flag before the groups guard so it is set even for a
  // user with no progress groups yet.
  const betaPathGate = data?.betaPathGate === true;
  // The API's totalWeight-weighted overall is the sole source for the band
  // tile and the chart's center percent.
  const overall = data?.overall ?? 0;
  if (!data?.groups) return { topics: [], overall, betaPathGate, blurbs };

  const partnerGroupById = new Map((data.partner?.groups || []).map(g => [g.id, g]));

  const topics = data.groups.map(group => {
    const partnerGroup = partnerGroupById.get(group.id) || null;
    return {
      id: group.id,
      label: group.label,
      description: group.description,
      type: group.type, // 'core' | 'life'
      progress: group.progress,
      partnerProgress: partnerGroup ? partnerGroup.progress : null,
      sectionCount: group.sectionCount,
      completedSections: group.completedSections,
      sections: group.sections || []
    };
  });

  return { topics, overall, betaPathGate, blurbs };
}
